import {
    APIENV,
    DossierOpenSearch,
    getCustomiserState,
    useAnnoncesLegalesService,
    useFacturationService,
    useOffreService,
} from '@europrocurement/l2d-domain';
import { useRef } from 'react';
import { TblFactArticlesJsonldLigneDevisALRead } from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { useSnackbar } from 'notistack';
import {
    PrestationAnnonceLgaleApiGetTblPrestaPrestationAnnonceLgaleItemRequest,
    PrestationAnnonceLGaleJsonldPrestaRead,
} from '@europrocurement/l2d-domain/openApi/ApiAnnoncesLegales';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { AxiosResponse } from 'axios';
import useDomaine from '@europrocurement/l2d-modules/modules/ParcoursFormalite/hooks/useDomaine';
import useDossierProcesses from './useDossierProcesses';
import useDossierArticle, { type DossierArticle } from './useDossierArticle';

export const STATUS_DOSSIER_ONGOING = 4;

export type LegalAnnoucementType = {
    id: number;
    label: string;
};

type getArticlesFromLigneDevisAnoucementsProps = {
    idLegalAnnoucement: number;
    factArticleCategories?: number[];
};

export type articlePriceAndQuantityType = {
    article: TblFactArticlesJsonldLigneDevisALRead;
    price?: number | null;
    quantity?: number | null;
};
const useDossierLegalAnnoucements = () => {
    const { legalsAdvertApi, legalPrestationsApi } = useAnnoncesLegalesService();
    const { addArticle } = useDossierArticle();
    const { articlesApi } = useOffreService();
    const { ligneDevisAnnonceLegaleApi } = useFacturationService();
    const domaineId = useDomaine();
    const articlesFromLegalAnoucement = useRef<articlePriceAndQuantityType[]>([]);
    const rubfacsBodacc = ['FRN', 'FRS'];
    const { findProcessByArticle, updateValidatorProcess } = useDossierProcesses();
    const { enqueueSnackbar } = useSnackbar();

    const getLegalAnnoucementAppUrl = () =>
        APIENV.apps.annoncesLegales[getCustomiserState().app.annoncesLegales] || undefined;

    const getLegalAnnoucement = async (id: number): Promise<LegalAnnoucement> => {
        const result = await legalPrestationsApi.getTblPrestaPrestationAnnonceLgaleItem({
            id: id as unknown as string,
        });

        return {
            id: result.data.id as number,
            dossier: {
                id: result.data.dossier?.id as number,
                statut: result.data.dossier?.statut as number,
            },
            numero: result.data.numero as string,
            justif: result.data.justifLienPublic as string,
            lien: result.data.lien as string,
            journal: result.data.jal?.nom,
            annonceur: result.data.annonceur,
            date: result.data.dateDemande
                ? new Date(result.data.dateDemande).toLocaleDateString('FR-fr')
                : undefined,
            justifBodacc: result.data.bodacc?.bdcLienBodaccAvis as string,
            idDocumentAttestation: result.data.idDocumentAttestation,
        };
    };

    const getLegalAnnoucementFile = async (
        id: number,
    ): Promise<AxiosResponse<PrestationAnnonceLGaleJsonldPrestaRead>> =>
        await legalPrestationsApi.getTblPrestaAttestationPrestationAnnonceLgaleItem(
            {
                id: id as unknown as string,
            },
            { responseType: 'blob' },
        );

    const getLegalAnnoucementFileContent = async (prestaId: number): Promise<Blob> => {
        const content = await getLegalAnnoucementFile(prestaId as number);
        return content.data as unknown as Blob;
    };

    /**
     *
     * Fetch LigneDevis linked to the legalAnoucement
     * and return corresponding formalities factArticles
     *
     * @param param.idLegalAnnoucement
     * @param idLegalAnnoucement
     * @returns object status: number, articlesFromLegalAnoucement: TblFactArticlesJsonldLigneDevisALRead[]
     */
    const getFormalityArticlesFromLigneDevisAnoucements = async ({
        idLegalAnnoucement,
    }: getArticlesFromLigneDevisAnoucementsProps) => {
        // Get all articles linked to the legalAnouncement from ligneDevis
        articlesFromLegalAnoucement.current = [];

        const { status, data } =
            await ligneDevisAnnonceLegaleApi.apiLigneDevisAnnonceLegaleGetCollection({
                idAnnonce: idLegalAnnoucement,
            });

        // const articlesLigneDevisBodacc = data['hydra:member'].filter(
        //     (ligneDevis) =>
        //         ligneDevis.article?.idCatArticle &&
        //         factArticleCategories.includes(ligneDevis.article.idCatArticle['@id']),
        // );
        // Get corresponding id article between formality and legal anouncement
        data['hydra:member'].map((articleLigneDevis) =>
            articleLigneDevis.article?.articlesLinked?.map((artLinked) => {
                if (artLinked?.ddm?.ddmId === domaineId || artLinked?.ddm?.ddmId === 0) {
                    articlesFromLegalAnoucement.current = [
                        ...articlesFromLegalAnoucement.current,
                        {
                            article: artLinked,
                            quantity: articleLigneDevis.qte,
                            price: articleLigneDevis.prixbase,
                        },
                    ];
                    return artLinked;
                }
                return artLinked;
            }),
        );

        return { status, articlesFromLegalAnoucement: articlesFromLegalAnoucement.current };
    };

    const getLegalAnnoucementTypes = async (): Promise<Array<LegalAnnoucementType>> => {
        const response = await legalsAdvertApi.getListAnnoncesLegalesAnnonceLegaleCollection({});
        const items = response.data['hydra:member'] || [];

        return items.map((item) => ({
            id: item.id as number,
            label: item.libelle as string,
        }));
    };

    const getPrestaLegalAnouncement = async (
        prestationId: string,
        xIdSociete: number = 12,
    ): Promise<PrestationAnnonceLGaleJsonldPrestaRead> => {
        const requestParams: PrestationAnnonceLgaleApiGetTblPrestaPrestationAnnonceLgaleItemRequest =
            {
                id: prestationId,
                xIdSociete,
            };
        const response =
            await legalPrestationsApi.getTblPrestaPrestationAnnonceLgaleItem(requestParams);
        return response.data || undefined;
    };

    const addLegalAnnoucement = async (
        title: string,
        prestationId: number,
        typeArticleId: number,
    ): Promise<number> => {
        const response = await articlesApi.apiArticlesGetCollection({
            xIdSociete: 12,
            domaineId: 3,
            rubArticle: 'PUB',
        });

        const baseArticle = response.data['hydra:member'][0] || undefined;

        if (!baseArticle) {
            throw new Error('Base article not found');
        }

        const articleId = await addArticle({
            prestationId,
            typeArticleId,
            label: title,
            factArticleId: baseArticle.id as number,
        });
        return articleId;
    };

    const deleteLegalAnnoucement = async (
        article: DossierArticle,
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        successMessage?: string,
        errorMessage?: string,
        validatorProcessStatus: string = 'failed',
    ) => {
        try {
            const process = findProcessByArticle(dossier, [validatorTypeId], article.id);
            await updateValidatorProcess(
                process?.id as number,
                validatorProcessStatus,
                '',
                '',
                true,
            );
            enqueueSnackbar(successMessage || 'Votre annonce a bien été supprimée');
            return { status: 200 };
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                errorMessage || 'Une erreur est survenue durant la suppression de votre annonce',
                {
                    variant: 'error',
                },
            );
            return { status: 500 };
        }
    };

    return {
        getLegalAnnoucementAppUrl,
        getLegalAnnoucement,
        getPrestaLegalAnouncement,
        getLegalAnnoucementTypes,
        getFormalityArticlesFromLigneDevisAnoucements,
        getLegalAnnoucementFile,
        getLegalAnnoucementFileContent,
        addLegalAnnoucement,
        deleteLegalAnnoucement,
        rubfacsBodacc,
    };
};

export default useDossierLegalAnnoucements;
